import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserContext } from "../../../shared/context/userContext";
import EMPAccordion from "../../../shared/components/EMPAccordion/EMPAccordion";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  mapRouteOneIdPreferenceToDropDownSelection,
} from "../../../shared/utils/onboardingProgressUtil";
import RouteOneIdAccordion from "./RouteOne/RouteOneIdAccordion";
import RouteOneEpaAccordion from "./RouteOne/RouteOneEpaAccordion";
import RouteOneSettingsAccordion from "./RouteOne/RouteOneSettingsAccordion";
import accordionStyles from "../../../shared/components/EMPAccordion/EMPAccordionLincoln.module.scss";

const OnboardingFinancing = ({ savedRouteOneId }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const paCode = user?.dealerId || "";
  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;
  const userId = user?.userId || user?.commonName;

  const [expandedRouteOneId, setExpandedRouteOneId] = useState(false);
  const [expandedRouteOneEpa, setExpandedRouteOneEpa] = useState(false);
  const [expandedRouteOneSettings, setExpandedRouteOneSettings] =
    useState(false);

  const [routeOneDealerId, setRouteOneDealerId] = useState("");
  const [savedRouteOneDealerId, setSavedRouteOneDealerId] = useState("");
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    const mappedSavedRouteOneId =
      mapRouteOneIdPreferenceToDropDownSelection(savedRouteOneId);

    setRouteOneDealerId(mappedSavedRouteOneId);
    setSavedRouteOneDealerId(mappedSavedRouteOneId);
  }, [savedRouteOneId]);

  // Fetch RouteOne dealer IDs from backend
  const {
    data: routeOneDealers = [],
    isPending: isLoadingDealers,
    isError: hasDealersError,
  } = useQuery({
    queryKey: ["routeOneDealers", paCode],
    queryFn: () => DealerOnboardingClient.getRouteOneDealers(paCode),
    enabled: Boolean(paCode),
    select: (dealers) =>
      dealers.map((dealer) => ({
        value: dealer.routeOneDealerId,
        label: dealer.dealerLegalName
          ? `${dealer.routeOneDealerId} — ${dealer.dealerLegalName}`
          : dealer.routeOneDealerId,
      })),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Save mutation
  const saveRouteOneDealerIdMutation = useMutation({
    mutationFn: (selectedId) =>
      DealerOnboardingClient.saveRouteOneDealerId(dealerCommonId, {
        routeOneId: selectedId,
        updatedBy: userId,
      }),
    onSuccess: (_data, selectedId) => {
      setSavedRouteOneDealerId(selectedId);
      setSaveError(null);
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
    },
    onError: (err) => {
      setSaveError(
        err?.message || "Could not save RouteOne Dealer ID. Please try again."
      );
    },
  });

  const handleRouteOneIdAccordionChange = () =>
    setExpandedRouteOneId((previousExpanded) => !previousExpanded);

  const handleRouteOneEpaAccordionChange = () =>
    setExpandedRouteOneEpa((previousExpanded) => !previousExpanded);

  const handleRouteOneSettingsAccordionChange = () =>
    setExpandedRouteOneSettings((previousExpanded) => !previousExpanded);

  const canSave = Boolean(dealerCommonId) && Boolean(userId);

  const handleRouteOneDealerIdSave = () => {
    if (!canSave) {
      setSaveError("Missing dealer/user context.");
      return;
    }

    setSaveError(null);
    saveRouteOneDealerIdMutation.mutate(routeOneDealerId);
  };

  const routeOneIdComplete =
    Boolean(routeOneDealerId) && savedRouteOneDealerId === routeOneDealerId;

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle testId="financing-page-title" className="tw-mb-6">
        {t("DealerOnboarding.content.financing.title", "Financing")}
      </OnboardingPageTitle>

      <div data-testid="onboarding-financing">
        <EMPAccordion
          heading={t(
            "DealerOnboarding.content.financing.routeOneId.title",
            "RouteOne ID"
          )}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedRouteOneId}
          onChange={handleRouteOneIdAccordionChange}
          prefixIconType={routeOneIdComplete ? "check" : "caution"}
          toggleIconColor="grey"
        >
          <RouteOneIdAccordion
            routeOneDealerIds={routeOneDealers}
            isLoading={isLoadingDealers}
            hasError={hasDealersError}
            selectedRouteOneDealerId={routeOneDealerId}
            onRouteOneDealerIdChange={setRouteOneDealerId}
            savedRouteOneDealerId={savedRouteOneDealerId}
            onSave={handleRouteOneDealerIdSave}
            isSaving={saveRouteOneDealerIdMutation.isPending}
            saveError={saveError}
          />
        </EMPAccordion>

        <EMPAccordion
          heading={t(
            "DealerOnboarding.content.financing.routeOneEpa.title",
            "Electonic Participating Agreement (ePA)"
          )}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedRouteOneEpa}
          onChange={handleRouteOneEpaAccordionChange}
          prefixIconType="caution"
          toggleIconColor="grey"
        >
          <RouteOneEpaAccordion />
        </EMPAccordion>

        <EMPAccordion
          heading={t(
            "DealerOnboarding.content.financing.routeOneSettings.title",
            "RouteOne Settings"
          )}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedRouteOneSettings}
          onChange={handleRouteOneSettingsAccordionChange}
          prefixIconType="caution"
          toggleIconColor="grey"
        >
          <RouteOneSettingsAccordion />
        </EMPAccordion>
      </div>
    </div>
  );
};

OnboardingFinancing.propTypes = {
  savedRouteOneId: PropTypes.string,
};

OnboardingFinancing.defaultProps = {
  savedRouteOneId: "",
};

export default OnboardingFinancing;
